import React, { useEffect, useState } from 'react';
import { LEADS_TEST_MODE, LEAD_STATUSES, LEAD_CHANNELS, rpcError } from './config';
import { L, Chips } from './ui';

// Colores de las gráficas (validados con el validador de paletas en tema claro y oscuro, incl. daltonismo):
// dorado = recibidos / barras de una serie, azul = reservas.
const C_MAIN = '#B8893A';
const C_SECOND = '#3B82F6';
// Semáforo de tiempos de atención (minutos). Siempre con icono y texto, nunca solo color.
export const TIME_GREEN_MAX = 15;
export const TIME_AMBER_MAX = 60;

const PERIODS = [
  { key: 'hoy', label: 'Hoy' }, { key: '7d', label: '7 días' }, { key: '30d', label: '30 días' },
  { key: 'mes', label: 'Este mes' }, { key: 'custom', label: 'Personalizado' }
];

const dayStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function periodRange(key, from, to) {
  const now = new Date();
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setDate(end.getDate() + 1);
  if (key === '7d') start.setDate(start.getDate() - 6);
  if (key === '30d') start.setDate(start.getDate() - 29);
  if (key === 'mes') start.setDate(1);
  if (key === 'custom') {
    if (!from || !to) return null;
    const f = new Date(`${from}T00:00:00`); const t = new Date(`${to}T00:00:00`); t.setDate(t.getDate() + 1);
    return t > f ? { from: f, to: t } : null;
  }
  return { from: start, to: end };
}

export function formatMinutes(m) {
  if (m == null) return '—';
  const n = Number(m);
  if (n < 60) return `${n.toLocaleString('es-ES', { maximumFractionDigits: 1 })} min`;
  const h = Math.floor(n / 60); const r = Math.round(n % 60);
  if (h < 24) return `${h} h${r ? ` ${r} min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} h`;
}

function timeStatus(m) {
  if (m == null) return null;
  if (m < TIME_GREEN_MAX) return { icon: '🟢', text: 'Rápido', color: 'var(--t-4ade80)' };
  if (m <= TIME_AMBER_MAX) return { icon: '🟡', text: 'Mejorable', color: 'var(--t-fbbf24)' };
  return { icon: '🔴', text: 'Lento', color: 'var(--t-f87171)' };
}

const pct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %`);

// Métricas y KPIs (solo creador y responsable): llama a la RPC lead_metrics.
export default function Metrics({ supabase, refreshKey }) {
  const [period, setPeriod] = useState('30d');
  const [from, setFrom] = useState(() => dayStr(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(() => dayStr(new Date()));
  const [includeTest, setIncludeTest] = useState(LEADS_TEST_MODE);
  const [channel, setChannel] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const range = periodRange(period, from, to);
  const rangeKey = range ? `${range.from.getTime()}-${range.to.getTime()}` : '';

  useEffect(() => {
    if (!range) return undefined;
    let cancelled = false;
    setLoading(true);
    supabase.rpc('lead_metrics', { p_from: range.from.toISOString(), p_to: range.to.toISOString(), p_include_test: includeTest, p_channel: channel || null })
      .then(({ data: d, error: e }) => {
        if (cancelled) return;
        setLoading(false);
        if (e) { setError(rpcError(e)); return; }
        setError(''); setData(d);
      });
    return () => { cancelled = true; };
  }, [supabase, rangeKey, includeTest, channel, refreshKey]);

  const t = data?.totales || {};
  const users = (data?.usuarios || []).filter(u => Number(u.asignados) > 0 || Number(u.interacciones) > 0);
  const attend = timeStatus(t.min_hasta_atender);

  return (
    <div style={{ display: 'grid', gap: '12px', minWidth: 0 }}>
      <div style={{ ...L.section, marginBottom: 0, display: 'grid', gap: '8px' }}>
        <Chips small value={period} onChange={setPeriod} options={PERIODS} />
        <Chips small value={channel} onChange={setChannel} options={[
          { key: '', label: 'Todos los canales' },
          ...LEAD_CHANNELS.map(c => ({ key: c.key, label: `${c.icon} ${c.label}` }))
        ]} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'center' }}>
          {period === 'custom' && (
            <>
              <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>Desde
                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...L.input, marginBottom: 0, width: 'auto', padding: '5px 8px' }} />
              </label>
              <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>hasta
                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...L.input, marginBottom: 0, width: 'auto', padding: '5px 8px' }} />
              </label>
            </>
          )}
          {LEADS_TEST_MODE && (
            <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />
              Incluir leads de prueba
            </label>
          )}
          {loading && <span style={L.muted}>Calculando…</span>}
        </div>
      </div>

      {!range && <div style={L.warn}>Elige un periodo válido.</div>}
      {error && <div style={L.error}>{error}</div>}
      <p style={{ ...L.muted, margin: 0 }}>
        ⏱️ Los tiempos se miden desde la <strong>hora de entrada</strong> (en el CRM para los online; de llegada del cliente o de la llamada para los presenciales y las llamadas a tienda). "Atendido" = primer
        contacto registrado (llamada, WhatsApp, email o visita; las notas no cuentan). El periodo se aplica a la hora de entrada.
      </p>

      {data && (
        <>
          {data.por_canal && <ChannelCompare byChannel={data.por_canal} />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))', gap: '8px' }}>
            <Tile label="Leads recibidos" value={t.recibidos ?? 0} />
            <Tile label="Sin asignar" value={t.sin_asignar ?? 0} sub="del periodo, aún abiertos" />
            <Tile label="Tiempo medio hasta atender" value={formatMinutes(t.min_hasta_atender)}
              sub={`Mediana ${formatMinutes(t.mediana_min_hasta_atender)}`} status={attend} />
            <Tile label="Conversión a reserva" value={pct(t.pct_reserva)} sub={`${t.reservas ?? 0} reservas`} />
            <Tile label="Vendidos" value={pct(t.pct_vendidos)} sub={`${t.vendidos ?? 0} leads`} />
            <Tile label="Perdidos" value={pct(t.pct_perdidos)} sub={`${t.perdidos ?? 0} leads`} />
            <Tile label="Contactados" value={pct(t.pct_contactados)} sub={`${t.atendidos ?? 0} atendidos`} />
            <Tile label="Tiempo medio hasta asignar" value={formatMinutes(t.min_hasta_asignar)} />
            <Tile label="Seguimientos vencidos" value={t.vencidos ?? 0} />
            <Tile label="Descartados" value={t.descartados ?? 0} sub="no cuentan en lo demás" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '12px' }}>
            <Panel title="Embudo por estado">
              <Bars rows={LEAD_STATUSES.filter(s => s.key !== 'descartado').map(s => ({ label: s.label, value: Number(data.embudo?.[s.key] || 0) }))} unit="leads" />
            </Panel>
            <Panel title="Quién atiende más">
              {users.length === 0 ? <p style={L.muted}>Nadie ha atendido leads en este periodo.</p> : (
                <Bars rows={users.map(u => ({ label: (u.full_name || '').trim() || 'Usuario', value: Number(u.atendidos) }))} unit="atendidos" />
              )}
            </Panel>
          </div>

          <Panel title="Recibidos y reservas por día">
            <DailyChart rows={data.por_dia || []} range={range} />
          </Panel>

          <Panel title="KPIs por persona">
            <KpiTable users={users} />
          </Panel>
        </>
      )}
    </div>
  );
}

// Los tres canales lado a lado (siempre todos, aunque haya un canal filtrado).
function ChannelCompare({ byChannel }) {
  const rows = [
    { label: 'Recibidos', get: c => c.recibidos ?? 0 },
    { label: 'Contactados', get: c => pct(c.pct_contactados) },
    { label: 'Tiempo medio hasta atender', get: c => formatMinutes(c.min_hasta_atender) },
    { label: 'Conversión a reserva', get: c => `${pct(c.pct_reserva)} (${c.reservas ?? 0})` },
    { label: 'Vendidos', get: c => `${pct(c.pct_vendidos)} (${c.vendidos ?? 0})` },
    { label: 'Perdidos', get: c => c.perdidos ?? 0 }
  ];
  const cell = { padding: '6px 8px', borderBottom: '1px solid var(--l-334155)', textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <Panel title="Por canal: online, presencial y llamada a tienda">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', color: 'var(--t-cbd5e1)' }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: 'left' }} scope="col"><span style={L.muted}>Del periodo</span></th>
              {LEAD_CHANNELS.map(c => <th key={c.key} style={{ ...cell, color: 'var(--t-f1f5f9)' }} scope="col">{c.icon} {c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.label}>
                <th style={{ ...cell, textAlign: 'left', fontWeight: 500 }} scope="row">{r.label}</th>
                {LEAD_CHANNELS.map(c => <td key={c.key} style={cell}>{r.get(byChannel[c.key] || {})}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ ...L.section, marginBottom: 0, minWidth: 0 }}>
      <h5 style={L.sectionTitle}>{title}</h5>
      {children}
    </div>
  );
}

function Tile({ label, value, sub, status }) {
  return (
    <div style={{ backgroundColor: 'var(--b-0f172a)', border: '1px solid var(--l-334155)', borderRadius: '10px', padding: '10px 12px', display: 'grid', gap: '2px' }}>
      <span style={{ color: 'var(--t-9ca3af)', fontSize: '0.75rem' }}>{label}</span>
      <strong style={{ color: 'var(--t-f1f5f9)', fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
      {status && <span style={{ color: status.color, fontSize: '0.75rem', fontWeight: 600 }}>{status.icon} {status.text}</span>}
      {sub && <span style={{ color: 'var(--t-9ca3af)', fontSize: '0.72rem' }}>{sub}</span>}
    </div>
  );
}

// Barras horizontales de una serie (HTML): etiqueta, barra y valor en texto (el valor no va en color).
function Bars({ rows, unit }) {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div style={{ display: 'grid', gap: '6px' }}>
      {rows.map(r => (
        <div key={r.label} title={`${r.label}: ${r.value} ${unit}`}
          style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, 34%) 1fr 34px', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
          <span style={{ color: 'var(--t-cbd5e1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
          <div style={{ height: '12px', backgroundColor: 'var(--b-1e293b)', borderRadius: '4px', overflow: 'hidden' }}>
            {r.value > 0 && <div style={{ width: `${(r.value / max) * 100}%`, height: '100%', backgroundColor: C_MAIN, borderRadius: '0 4px 4px 0' }} />}
          </div>
          <span style={{ color: 'var(--t-e5e7eb)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// Columnas agrupadas por día (SVG): recibidos (dorado) y reservas (azul), con leyenda y valores al pasar el ratón.
function DailyChart({ rows, range }) {
  const [hover, setHover] = useState(null);
  const byDay = Object.fromEntries(rows.map(r => [r.dia, r]));
  const days = [];
  if (range) {
    const d = new Date(range.from);
    while (d < range.to && days.length < 62) { days.push(dayStr(d)); d.setDate(d.getDate() + 1); }
  }
  if (days.length === 0) return <p style={L.muted}>Sin datos.</p>;
  const series = days.map(k => ({ day: k, rec: Number(byDay[k]?.recibidos || 0), res: Number(byDay[k]?.reservas || 0) }));
  const max = Math.max(1, ...series.map(s => Math.max(s.rec, s.res)));
  const W = Math.max(280, days.length * 22); const H = 150; const pad = { l: 26, r: 6, t: 8, b: 22 };
  const slot = (W - pad.l - pad.r) / days.length;
  const bw = Math.max(3, Math.min(10, slot / 2 - 2));
  const y = (v) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const ticks = [0, Math.ceil(max / 2), max].filter((v, i, a) => a.indexOf(v) === i);
  const labelEvery = Math.ceil(days.length / 8);
  const h = hover != null ? series[hover] : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: '14px', fontSize: '0.75rem', color: 'var(--t-cbd5e1)', marginBottom: '6px', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: C_MAIN, marginRight: 5 }} />Recibidos</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: C_SECOND, marginRight: 5 }} />Reservas</span>
        <span style={{ marginLeft: 'auto', color: 'var(--t-9ca3af)' }}>
          {h ? `${h.day.split('-').reverse().slice(0, 2).join('/')}: ${h.rec} recibidos · ${h.res} reservas` : 'Pasa el ratón o toca una barra'}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={W} height={H} role="img" aria-label="Leads recibidos y reservas por día" style={{ display: 'block' }}>
          {ticks.map(v => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--l-334155)" strokeWidth="1" />
              <text x={pad.l - 4} y={y(v) + 3} textAnchor="end" fontSize="9" fill="var(--t-9ca3af)">{v}</text>
            </g>
          ))}
          {series.map((s, i) => {
            const x0 = pad.l + i * slot + slot / 2;
            return (
              <g key={s.day} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => setHover(i)}>
                <rect x={pad.l + i * slot} y={pad.t} width={slot} height={H - pad.t - pad.b} fill={hover === i ? 'var(--b-334155)' : 'transparent'} opacity="0.5" />
                {s.rec > 0 && <rect x={x0 - bw - 1} y={y(s.rec)} width={bw} height={y(0) - y(s.rec)} rx="2" fill={C_MAIN} />}
                {s.res > 0 && <rect x={x0 + 1} y={y(s.res)} width={bw} height={y(0) - y(s.res)} rx="2" fill={C_SECOND} />}
                {i % labelEvery === 0 && (
                  <text x={x0} y={H - 6} textAnchor="middle" fontSize="9" fill="var(--t-9ca3af)">{s.day.slice(8)}/{s.day.slice(5, 7)}</text>
                )}
                <title>{`${s.day.split('-').reverse().join('/')}: ${s.rec} recibidos, ${s.res} reservas`}</title>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

const KPI_COLS = [
  ['asignados', 'Asignados'], ['atendidos', 'Atendidos'], ['pct_contactados', '% contactados', 'pct'],
  ['min_asignacion_a_contacto', 'Asignación → contacto', 'min'], ['min_entrada_a_contacto', 'Entrada → contacto', 'min'],
  ['reservas', 'Reservas'], ['pct_reserva', '% reserva', 'pct'], ['vendidos', 'Vendidos'], ['perdidos', 'Perdidos'],
  ['vencidos', 'Vencidos'], ['interacciones', 'Contactos']
];

function KpiTable({ users }) {
  const [sort, setSort] = useState({ key: 'atendidos', dir: -1 });
  if (users.length === 0) return <p style={L.muted}>Sin datos en este periodo.</p>;
  const rows = [...users].sort((a, b) => {
    const va = a[sort.key] == null ? -Infinity : Number(a[sort.key]);
    const vb = b[sort.key] == null ? -Infinity : Number(b[sort.key]);
    return (va - vb) * sort.dir;
  });
  const th = { padding: '6px 8px', color: 'var(--t-9ca3af)', fontWeight: 600, fontSize: '0.72rem', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid var(--l-334155)' };
  const td = { padding: '6px 8px', color: 'var(--t-e5e7eb)', fontSize: '0.8rem', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--l-334155)' };
  const fmt = (v, kind) => (kind === 'pct' ? pct(v) : kind === 'min' ? formatMinutes(v) : (v ?? 0));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }}>Persona</th>
            {KPI_COLS.map(([k, label]) => (
              <th key={k} style={th}>
                <button type="button" onClick={() => setSort(s => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}
                  style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer', padding: 0 }}>
                  {label}{sort.key === k ? (sort.dir < 0 ? ' ▼' : ' ▲') : ''}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(u => (
            <tr key={u.profile_id}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{(u.full_name || '').trim() || 'Usuario'}</td>
              {KPI_COLS.map(([k, , kind]) => {
                const st = kind === 'min' ? timeStatus(u[k]) : null;
                return <td key={k} style={td}>{st ? `${st.icon} ` : ''}{fmt(u[k], kind)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ ...L.muted, margin: '8px 0 0' }}>
        🟢 menos de {TIME_GREEN_MAX} min · 🟡 hasta {TIME_AMBER_MAX} min · 🔴 más de {TIME_AMBER_MAX} min. "Contactos" = llamadas, WhatsApp,
        emails y visitas registrados en el periodo. Pulsa una columna para ordenar.
      </p>
    </div>
  );
}
